"use client";

import { fmtKwh } from "@/lib/site-energy";

/**
 * Every energy figure has a cost beside it (feature C2).
 *
 * A kWh on a screen is either priced, in which case the pounds sit beside it
 * with the status of the rate that produced them, or it is not, in which case
 * the sentence saying why sits beside it instead. There is no third state and
 * there is no empty gap: "0.00" and "not priced" are different facts.
 *
 * `Kwh` renders the quantity and its `CostTag` as one unit so a page cannot
 * print the number and forget the money. The guard in
 * scripts/__tests__/pounds-beside-kwh.test.mjs fails any demo path page that
 * prints "kWh" outside this component or without a cost element beside it.
 */

export type CostBeside =
  | { priced: true; gbp: number; status?: string | null; source?: string | null; version?: string | null }
  | { priced: false; reason: string };

export const notPriced = (reason: string): CostBeside => ({ priced: false, reason: reason.startsWith("not priced") ? reason : `not priced: ${reason}` });

/** The same sentence backend.core.pricing uses when a site has no MPAN row. */
export const NO_MPAN_REASON = "not priced: no MPAN is configured for this site, so the charge library cannot be applied";
/** Window totals sum half hours that fall in different DUoS bands; only the half hourly series carries a price per line. */
export const AGGREGATE_REASON = "not priced: sums half hours across DUoS bands; priced lines are on the half-hourly self-consumption view";
/** A rating, not a flow: nothing was bought or sold. */
export const RATING_REASON = "not priced: a capacity rating, not an energy flow";
/** Energy that never crossed the grid meter is not bought or sold at the MPAN. */
export const BEHIND_METER_REASON = "not priced: not metered at the grid boundary, so no import or export price applies";
/** A modelled figure is not a settled quantity. */
export const MODELLED_REASON = "not priced: a modelled quantity, not a metered flow";

export const fmtGbp = (v: number | null | undefined, digits = 2): string =>
  v === null || v === undefined || !Number.isFinite(v)
    ? "not priced"
    : `${v < 0 ? "-" : ""}£${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export function CostTag({ cost, className }: { cost: CostBeside | null | undefined; className?: string }) {
  if (!cost) {
    return (
      <span data-cost="unpriced" className={`text-amber-200 ${className ?? ""}`}>
        not priced: no rate lookup was made
      </span>
    );
  }
  if (!cost.priced) {
    return (
      <span data-cost="unpriced" className={`text-amber-200 ${className ?? ""}`} title={cost.reason}>
        {cost.reason}
      </span>
    );
  }
  const title = [cost.status, cost.source, cost.version ? `version ${cost.version}` : null].filter(Boolean).join(", ");
  return (
    <span data-cost="priced" className={`text-emerald-300 ${className ?? ""}`} title={title || undefined}>
      {fmtGbp(cost.gbp)}
      {cost.status ? <span className="ml-1 text-[10px] text-slate-500">({cost.status})</span> : null}
    </span>
  );
}

/** A kWh figure and its cost, rendered together. `kwh` null renders "not available" and no cost. */
export function Kwh({
  kwh,
  cost,
  digits = 1,
  block,
}: {
  kwh: number | null | undefined;
  cost: CostBeside | null | undefined;
  digits?: number;
  /** Stack the cost under the figure instead of beside it (tiles). */
  block?: boolean;
}) {
  if (kwh === null || kwh === undefined || !Number.isFinite(kwh)) {
    return <span data-energy="kwh">not available</span>;
  }
  return (
    <span className={block ? "inline-flex flex-col" : "inline-flex flex-wrap items-baseline gap-x-2"}>
      <span data-energy="kwh">{fmtKwh(kwh, digits)}</span>
      <CostTag cost={cost} className={block ? "text-xs" : "text-[11px]"} />
    </span>
  );
}

/** Sum priced costs; if any is unpriced, the sum is unpriced with the count and first reason. */
export function sumCosts(costs: Array<CostBeside | null | undefined>): CostBeside {
  let total = 0;
  let unpriced = 0;
  let firstReason: string | null = null;
  let status: string | null = null;
  for (const c of costs) {
    if (!c || !c.priced) {
      unpriced += 1;
      if (firstReason === null) firstReason = c ? c.reason : "not priced: no rate lookup was made";
      continue;
    }
    total += c.gbp;
    if (c.status && status !== c.status) status = status ? "mixed" : c.status;
  }
  if (costs.length === 0) return notPriced("nothing to price");
  if (unpriced > 0) return { priced: false, reason: `not priced: ${unpriced} of ${costs.length} half hours unpriced (${firstReason})` };
  return { priced: true, gbp: Math.round(total * 100) / 100, status };
}
